/**
 *
 * InputField
 *
 * ------------
 *
 * The prop `labelClassName` is needed to override the border color behind
 * the label text.
 *
 * ex:
 *
 * passing 'channel-page__field-label' as `labelClassName`
 * and creating the CSS (SCSS) rule:
 *
 * ```css
 * .input-field__label.channel-page__field-label:before {
 *   border-top: 1px solid #000000;
 * }
 * ```
 *
 * allows the line behind the label to be #000000
 *
 * NOTE: this color should match the color of the background to
 * correctly reflect the mockups
 *
 * ------------
 *
 */

import React, { useState, useEffect, useRef, useId } from "react";
import {
  EditBtn,
  RightIcon,
  InPlaceBtn,
  InPlaceButtons,
  Input,
  Label,
  PasswordButton,
  SelectIcon,
  TextArea,
  Wrapper,
  StyledRPS,
} from "./styledComponents";

import Date from "./Date";
import File from "./File";
import Icon from "components/Icon";
import InputValidator from "components/InputValidator";
import Select from "./Select";
import { colors } from "globalStyles";
import Tooltip from "components/Tooltip";

const InputField = (props) => {
  const {
    inPlace,
    field: { onFocus: inputOnFocus, onChange: inputOnChange, ...input },
    label,
    editBtnText,
    updateBtnText,
    customActionBtn,
    type,
    placeholder,
    autoComplete,
    inputClassName,
    inputStyle,
    labelClassName,
    iconClassName,
    validIconName,
    errorIconName,
    emptyIconName,
    onFocus,
    onBlur,
    onChange,
    inPlaceOnSubmit,
    showPasswordButton,
    helpEvent,
    helpIcon,
    disabled,
    multiline,
    leftIcon,
    validateIcon,
    tooltip,
    errorMsg,
    isSignup = false,
    "aria-describedby": ariaDescribedBy,
  } = props;

  // Stable, unique id linking the visible <Label> to its input via
  // htmlFor/id, so the label is the input's accessible name (WCAG 1.3.1 /
  // 4.1.2). Callers may pass an explicit `id`; otherwise useId() guarantees
  // uniqueness across the many InputFields on a page.
  const reactId = useId();
  const fieldId = props.id || reactId;

  const [showPassword, setShowPassword] = useState(true);
  const [inPlaceOpen, setInPlaceOpen] = useState(false);
  const [hasPasswordError, setHasPasswordError] = useState(false);
  const [password, setPassword] = useState("");
  const [initialValue, setInitialValue] = useState(null);

  const inputFieldRef = useRef(null);

  const togglePasswordType = () => setShowPassword(!showPassword);

  const toggleInPlace = () => {
    setInPlaceOpen(!inPlaceOpen);
    if (!inPlaceOpen && inputFieldRef.current && inputFieldRef.current.focus) {
      inputFieldRef.current.focus();
    }
  };

  useEffect(() => {
    if (initialValue === null && typeof props.field.value !== "undefined") {
      setInitialValue(props.field.value);
    }
  }, [props.field.value]);

  // Gallery-URL availability probe. Always probes the value the user actually
  // typed: the availability endpoint excludes the channel being edited (it
  // receives that channel's id), so an unchanged slug — and any slug from this
  // channel's own append-only `slug_history` — correctly answers "available".
  // This replaced a workaround that probed a random junk string whenever the
  // value equalled the field's initial value; that masked the current slug but
  // left the channel's history unreclaimable.
  useEffect(() => {
    if (props.field.name === "slug" && props.validateSlug && props.field.value) {
      props.validateSlug(props.field.value);
    }
  }, [props.field.value]);

  const handlePasswordStrength = (passwordStrength) => {
    const { isValid, password } = passwordStrength;
    if (isValid) {
      setHasPasswordError(false);
      setPassword(password);
    } else {
      setHasPasswordError(true);
      setPassword("••••••••••••");
    }
    return isValid ? inputOnChange(password) : inputOnChange(passwordStrength);
  };

  const cancel = () => {
    toggleInPlace();
  };

  const hasError = Boolean(errorMsg);
  const inPlaceAttr = inPlace && !inPlaceOpen ? { disabled: true } : {};
  const InputComponent = multiline ? TextArea : Input;

  return (
    <Wrapper className={inPlace ? "inPlace" : undefined}>
      {type === "password" ? (
        isSignup ? (
          <StyledRPS
            {...inPlaceAttr}
            minLength={8}
            minScore={2}
            innerRef={inputFieldRef}
            className="CustomInput"
            tooShortWord="too short"
            onFocus={onFocus || inputOnFocus}
            changeCallback={(state) => handlePasswordStrength(state)}
            inputProps={{
              ...input,
              id: fieldId,
              name: "password",
              type: showPassword ? "password" : "text",
              disabled: disabled,
              value: password,
              placeholder: placeholder,
            }}
            hasError={hasPasswordError}
          />
        ) : (
          <Input
            {...inPlaceAttr}
            id={fieldId}
            className={`${inputClassName} ${
              inPlace && !inPlaceOpen ? " inPlace " : ""
            }`}
            ref={inputFieldRef}
            hasError={hasError}
            {...input}
            value={input.value || ""}
            onBlur={onBlur || input.onBlur}
            onFocus={onFocus || inputOnFocus}
            onChange={onChange || inputOnChange}
            type={showPassword ? "password" : "text"}
            placeholder={placeholder}
            autoComplete={autoComplete || "on"}
            disabled={disabled}
            aria-describedby={ariaDescribedBy}
            style={inputStyle}
          />
        )
      ) : (
        <InputComponent
          {...inPlaceAttr}
          id={fieldId}
          className={`${inputClassName} ${leftIcon ? " has-icon " : ""} ${
            inPlace && !inPlaceOpen ? " inPlace " : ""
          }`}
          innerRef={inputFieldRef}
          hasError={hasError}
          {...input}
          value={input.value || ""}
          onBlur={onBlur || input.onBlur}
          onFocus={onFocus || inputOnFocus}
          onChange={onChange || inputOnChange}
          type={type}
          placeholder={placeholder}
          autoComplete={autoComplete || "on"}
          disabled={disabled}
          rows={multiline}
          aria-describedby={ariaDescribedBy}
          style={inputStyle}
        />
      )}
      {label && (
        <Label
          htmlFor={fieldId}
          className={`${labelClassName} ${multiline ? "multi-select" : ""}`}
        >
          {label}
        </Label>
      )}
      {leftIcon && <SelectIcon className="left-icon">{leftIcon}</SelectIcon>}
      {inPlace && !inPlaceOpen && (
        <EditBtn onClick={customActionBtn || toggleInPlace}>
          {editBtnText ? editBtnText : "Edit".toUpperCase()}
          {!editBtnText && <span> {label}</span>}
        </EditBtn>
      )}
      {inPlace && inPlaceOpen && (
        <InPlaceButtons>
          <InPlaceBtn
            className="cancel"
            onClick={() => {
              inputOnChange(initialValue);
              cancel();
            }}
          >
            cancel
          </InPlaceBtn>
          <InPlaceBtn
            className="submit"
            type="submit"
            onClick={() => {
              inPlaceOnSubmit({ [input.name]: input.value });
              toggleInPlace();
            }}
          >
            <span>{updateBtnText ? updateBtnText : `update ${label}`}</span>
            <span>confirm</span>
          </InPlaceBtn>
        </InPlaceButtons>
      )}
      <InputValidator
        hasError={hasError}
        isValid={!hasError}
        iconClassName={iconClassName}
        validIconName={validIconName}
        errorIconName={errorIconName}
        emptyIconName={emptyIconName}
      />
      {showPasswordButton && type === "password" && !inPlace && (
        <PasswordButton type="button" onClick={togglePasswordType}>
          {showPassword ? "show" : "hide"}
        </PasswordButton>
      )}
      {helpIcon && helpEvent && (
        <RightIcon type="button" aria-label="Help" onClick={helpEvent}>
          <Icon name="help" size="10" />
        </RightIcon>
      )}
      {validateIcon && (
        <RightIcon>
          <Icon name="check" size="10" fill={colors.GREEN} />
        </RightIcon>
      )}
      {tooltip && (
        <Tooltip className="tooltip" width="200px" text={tooltip}>
          <Icon size="12" name="help" />
        </Tooltip>
      )}
    </Wrapper>
  );
};

export default InputField;
export { Select, File, Date };
